import React, { useState, useRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { IoArrowUp } from "react-icons/io5";
import { maxMessageLength } from "../../config/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { customBranding, apiEndpoints, authToken, accessToken } from "../../config/config";
import { useChatbotConfig } from "../../contexts/ChatbotConfigContext";
import { getParentUrl } from "../../utils/parentUrlUtils";
import { useLanguage } from "../../contexts/LanguageContext"; // Import language context
import { useStaticTranslations } from "../../utils/staticTranslations"; // Import static translations
import { v4 as uuidv4 } from "uuid";
import ReminderForm from '../../components/Form/ReminderForm';

const ContactForm = React.lazy(() => import("../../components/Form/Contact"));
const StatusForm = React.lazy(() => import("../../components/Form/Status"));
const FeedbackForm = React.lazy(() => import("../../components/Form/Feedback"));
const Login = React.lazy(() => import("../../components/Form/Login"));
const Appointment = React.lazy(() =>
  import("../../components/Form/Appointment")
);
const Tasks = React.lazy(() => import("../../components/Form/Tasks"));

const Chat = React.memo(
  ({
    setMessages,
    messages,
    isWaitingForResponse,
    setIsWaitingForResponse,
    setIsTyping,
    setAbortController,
    currentChatId,
    isLoggedIn,
    setIsLoggedIn,
  }) => {
    const { conversation_id } = useParams();
    const [input, setInput] = useState("");
    const [showContactForm, setShowContactForm] = useState(false);
    const [showStatusForm, setShowStatusForm] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [showLoginForm, setShowLoginForm] = useState(false);
    const [showAppointmentForm, setShowAppointmentForm] = useState(false);
    const [showTasksForm, setShowTasksForm] = useState(false);
    const [showReminderForm, setShowReminderForm] = useState(false);
    const { config } = useChatbotConfig();
    const isExpandedWindow = window.opener != null;
    const [filteredCommands, setFilteredCommands] = useState([]);
    const [selectedCommandIndex, setSelectedCommandIndex] = useState(-1);
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const [autoScroll, setAutoScroll] = useState(true);
    const lastScrollHeight = useRef(0);
    // const [messages, setMessages] = useState([]);
    const lastScrollTop = useRef(0);
    const [parentUrl, setParentUrl] = useState(null);

     const { currentLanguage } = useLanguage(); // Get current language
    const { buttonLabel, commonMessage } = useStaticTranslations(currentLanguage.code);

    // Add new state for chatbot config
    const [chatbotApiConfig, setChatbotApiConfig] = useState(null);
    const [eventDetails, setEventDetails] = useState(null);

    useEffect(() => {
      // If conversation_id exists, fetch the chat details
      if (conversation_id) {
        const fetchChatDetails = async () => {
          try {
            const response = await axios.get(
              apiEndpoints.conversation_messages(conversation_id),
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                  Accept: "application/json",
                  clientToken: `Bearer ${accessToken}`,
                },
              }
            );
            console.log("Chat data:", response.data);
            // Here you can load the messages from the response
            if (response.data.messages) {
              setMessages(response.data.messages);
            }
          } catch (error) {
            console.error("Error fetching chat details:", error);
          }
        };

        fetchChatDetails();
      }
    }, [conversation_id]);

    // Close login form if user is already logged in
    useEffect(() => {
      if (isLoggedIn) {
        setShowLoginForm(false);
      }
      const fetchChatDetails = async () => {
        try {
          const response = await axios.get(
            `${apiEndpoints.conversation_messages}/${conversation_id}`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
                Accept: "application/json",
                clientToken: `Bearer ${accessToken}`,
              },
            }
          );
          console.log("Chat data:", response.data);
        } catch (error) {
          console.error("Error fetching chat details:", error);
        }
      };
      if (conversation_id) {
        fetchChatDetails();
      }
    }, [isLoggedIn, conversation_id]);

    const getAiChatMessages = () => {
      try {
        const savedData = localStorage.getItem("aiChatMessages");
        if (savedData) {
          return JSON.parse(savedData);
        }
        return createNewAiChat();
      } catch (error) {
        console.error("Error getting AI chat messages:", error);
        return createNewAiChat();
      }
    };

    const createNewAiChat = () => {
      const greetingMessage = localStorage.getItem("chatBotData")?.greetingMessage;
      const newChatId = uuidv4();
      const newAiChatObj = {
        id: newChatId,
        chatMessages: [
          {
            type: "bot",
            content: greetingMessage || "How can I help you today?",
            timestamp: new Date(),
          },
        ],
      };

      localStorage.setItem("aiChatMessages", JSON.stringify(newAiChatObj));
      return newAiChatObj;
    };

    const updateAiChatMessages = (newMessage) => {
      const aiChatData = getAiChatMessages();

      // Add the new message
      aiChatData.chatMessages.push(newMessage);

      // Save back to localStorage
      localStorage.setItem("aiChatMessages", JSON.stringify(aiChatData));

      return aiChatData;
    };

    // Add new useEffect to fetch chatbot config
    useEffect(() => {
      const fetchChatbotConfig = async () => {
        try {
          // const response = await axios.get(apiEndpoints.chatbot_config, {
          //   headers: {
          //     clientToken: `Bearer ${accessToken}`,
          //     Accept: "application/json",
          //   },
          // });

          // setChatbotApiConfig(response.data);
          // setEventDetails(response.data.eventDetails);
        } catch (error) {
          console.error("Error fetching chatbot config:", error);
        }
      };

      fetchChatbotConfig();
    }, [accessToken]);

    // Effect to get and store parent URL
    useEffect(() => {
      const fetchParentUrl = async () => {
        try {
          const url = await getParentUrl(isExpandedWindow);
          setParentUrl(url);
        } catch (error) {
          console.error("Error getting parent URL:", error);
        }
      };
      fetchParentUrl();
    }, [isExpandedWindow]);

    useEffect(() => {
      if (!isWaitingForResponse) {
        inputRef.current?.focus();
      }
    }, [isWaitingForResponse]);
    // Add state for auth data
    // const [authData, setAuthData] = useState({
    //   accessToken: null,
    //   url: null
    // });

    // Get auth data from parent window
    // Get auth data from parent window
    // useEffect(() => {
    //   const handleAuthMessage = (event) => {
    //     if (event.data.type === 'UPDATE_CONFIG') {
    //       setAuthData({
    //         accessToken: event.data.payload.accessToken,
    //         url: window.location.href
    //       });
    //     }
    //   };

    //   window.addEventListener('message', handleAuthMessage);
    //   return () => window.removeEventListener('message', handleAuthMessage);
    // }, []);

    const commands = React.useMemo(() => {
      return isLoggedIn
        ? [
            { name: "status", description: buttonLabel('status') },
            { name: "tasks", description: buttonLabel('tasks') },
          ]
        : [
            { name: "contact", description: buttonLabel('contact') },
            { name: "feedback", description: buttonLabel('feedback') },
            { name: "login", description: buttonLabel('login') },
            { name: "appointment", description: buttonLabel('appointment') },
            { name: "reminder", description: buttonLabel('reminder') },
          ];
    }, [isLoggedIn, buttonLabel]);

    // Update login success handler to use the prop
    const handleLoginSuccess = useCallback(() => {
      setIsLoggedIn(true);
      setShowLoginForm(false);

      // Check for notification permission
      if (Notification.permission === "default") {
        // Request permission
        Notification.requestPermission().then((permission) => {
          if (permission === "denied") {
            toast.info("You have blocked notifications.");
          }
        });
      } else if (Notification.permission === "denied") {
        toast.info("You have blocked notifications.");
      }
    }, [setIsLoggedIn]);

    useEffect(() => {
      console.log("showContactForm:", showContactForm);
      console.log("showStatusForm:", showStatusForm);
    }, [showContactForm, showStatusForm]);
    // const toggleContactForm = useCallback(() => {

    //   setShowContactForm(true);
    //   setShowStatusForm(false);
    //   setShowFeedbackForm(false);
    //   setShowLoginForm(false);
    // }, []);
    const toggleContactForm = useCallback(() => {
      if (config.isContactForm === 0) {
        toast.info("Contact form is currently inactive");
        return;
      }
      setShowContactForm(true);
      setShowStatusForm(false);
    }, [config.isContactForm]);

    const toggleFeedbackForm = useCallback(() => {
      setShowFeedbackForm(true);
      setShowStatusForm(false);
      setShowContactForm(false);
      setShowLoginForm(false);
    }, []);

    const toggleAppointmentForm = useCallback(() => {
      setShowAppointmentForm(true);
      setShowContactForm(false);
      setShowStatusForm(false);
      setShowFeedbackForm(false);
      setShowLoginForm(false);
    }, []);

    const toggleStatusForm = useCallback(() => {
      setShowStatusForm(true);
      setShowAppointmentForm(false);
      setShowFeedbackForm(false);
      setShowContactForm(false);
      setShowLoginForm(false);
    }, []);
    const toggleTasksForm = useCallback(() => {
      setShowTasksForm(true);
      setShowAppointmentForm(false);
      setShowStatusForm(false);
      setShowFeedbackForm(false);
      setShowContactForm(false);
      setShowLoginForm(false);
    }, []);

    const handleInputChange = useCallback(
      (e) => {
        const value = e.target.value;
        setInput(value);



        if (
          value === "/" &&
          (showContactForm ||
            showStatusForm ||
            showFeedbackForm ||
            showLoginForm ||
            showAppointmentForm ||
            showTasksForm ||
            showReminderForm)  // Add this line
        ) {
          console.log("Closing forms");
          setShowContactForm(false);
          setShowStatusForm(false);
          setShowFeedbackForm(false);
          setShowLoginForm(false);
          setShowAppointmentForm(false);
          setShowTasksForm(false);
          setShowReminderForm(false);  // Add this line
          return;
        } else if (value.startsWith("/")) {
          const searchTerm = value.slice(1).toLowerCase();
          const filtered = commands.filter((command) =>
            command.name.toLowerCase().startsWith(searchTerm)
          );
          setFilteredCommands(filtered);
          setShowSuggestions(filtered.length > 0);
          setSelectedCommandIndex(-1);

          if (filtered.length === 1) {
            setSelectedCommandIndex(0);
            setInput(`/${filtered[0].name}`);
          }
        } else {
          setShowSuggestions(false);
          setSelectedCommandIndex(-1);
        }
      },
      [
        commands,
        showContactForm,
        showStatusForm,
        showFeedbackForm,
        showLoginForm,
        showAppointmentForm,
        showTasksForm,
      ]
    );

    const handleKeyDown = useCallback(
      (e) => {
        if (showSuggestions) {
          switch (e.key) {
            case "ArrowDown":
              e.preventDefault();
              setSelectedCommandIndex((prevIndex) =>
                prevIndex < filteredCommands.length - 1
                  ? prevIndex + 1
                  : prevIndex
              );
              break;
            case "ArrowUp":
              e.preventDefault();
              setSelectedCommandIndex((prevIndex) =>
                prevIndex > 0 ? prevIndex - 1 : prevIndex
              );
              break;
            case "Enter":
              e.preventDefault();
              if (selectedCommandIndex !== -1) {
                handleCommandClick(filteredCommands[selectedCommandIndex].name);
              }
              break;
            default:
              break;
          }
        }
      },
      [
        filteredCommands,
        selectedCommandIndex,
        showSuggestions,
        handleCommandClick,
      ]
    );


    useEffect(() => {
      const handleSetInput = (event) => {
        if (event.detail) {
          setInput(event.detail);
          // Auto-trigger the command if it's a reminder command
          if (event.detail === '/reminder') {
            setTimeout(() => {
              handleCommandClick('reminder');
            }, 100);
          }
        }
      };
      window.addEventListener('setInput', handleSetInput);
      return () => window.removeEventListener('setInput', handleSetInput);
    }, [handleCommandClick]);

    const handleCommandClick = useCallback(
      (command) => {
        console.log("Command clicked:", command);
        
        // Close all other forms first
        setShowContactForm(false);
        setShowFeedbackForm(false);
        setShowStatusForm(false);
        setShowLoginForm(false);
        setShowAppointmentForm(false);
        setShowTasksForm(false);
        setShowReminderForm(false);
        
        if (command === "contact") {
          if (!isLoggedIn && config.isContactForm !== 0) {
            setShowContactForm(true);
          } else {
            toast.info("You don't have access to the contact form");
          }
        } else if (command === "feedback") {
          if (!isLoggedIn) {
            setShowFeedbackForm(true);
          } else {
            toast.info("You don't have access the feedback form.");
          }
        } else if (command === "appointment") {
          if (!isLoggedIn) {
            setShowAppointmentForm(true);
          } else {
            toast.info("You don't have access the appointment form.");
          }
        } else if (command === "status") {
          if (isLoggedIn) {
            setShowStatusForm(true);
          } else {
            toast.error("Please login to access the status form.");
          }
        } else if (command === "tasks") {
          if (isLoggedIn) {
            setShowTasksForm(true);
          } else {
            toast.error("Please login to access tasks.");
          }
        } else if (command === "login") {
          if (!isLoggedIn) {
            setShowLoginForm(true);
          } else {
            toast.info("You are already logged in.");
          }
        } else if (command === "reminder") {
          // Open reminder form regardless of login status
          setShowReminderForm(true);
          
          // Add a bot message to guide the user
          setMessages(prev => [
            ...prev,
            {
              type: "bot",
              content: "📅 Great! Let's schedule a meeting reminder for you. Please fill in the details below and I'll make sure you get notified before your meeting.",
              timestamp: new Date(),
              isSystemMessage: true
            }
          ]);
        }
    
        setInput("");
        setShowSuggestions(false);
        setSelectedCommandIndex(-1);
      },
      [isLoggedIn, config.isContactForm, setMessages]
    );

    const handleSubmit = useCallback(
      async (e) => {
        e.preventDefault();
        if (input.trim() === "" || isWaitingForResponse) return;
    
        if (selectedCommandIndex !== -1) {
          handleCommandClick(filteredCommands[selectedCommandIndex].name);
          return;
        }
    
        if (input.startsWith("/")) {
          const command = input.slice(1).trim();
          handleCommandClick(command);
          return;
        }
    
        const truncatedInput = input.slice(0, maxMessageLength);
    
        const newMessage = {
          type: "user",
          content: truncatedInput,
          timestamp: new Date(),
        };
    
        const isAiChat =
          currentChatId &&
          localStorage.getItem("aiChatMessages") &&
          JSON.parse(localStorage.getItem("aiChatMessages")).id ===
            currentChatId;
    
        if (isAiChat) {
          // Update AI chat messages
          updateAiChatMessages(newMessage);
        }
    
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        setInput("");
    
        setIsTyping(true);
        setIsWaitingForResponse(true);
    
        const controller = new AbortController();
        setAbortController(controller);
    
        try {
          /*
          if (!parentUrl) {
            throw new Error("Parent URL not available");
          }
          const currentUrl = parentUrl || (await getParentUrl());
    
          console.log("Request details:", {
            accessToken: config.accessToken,
            currentUrl: currentUrl,
            sessionId: currentChatId,
            input: truncatedInput,
          });
    
          const apiUrl = process.env.REACT_APP_ENV === 'production' 
            ? currentUrl 
            : 'https://bolofy.com/';
    
          const response = await axios.post(
            apiEndpoints.chat,
            {
              content: truncatedInput,
              sessionId: currentChatId,
              url: apiUrl,
            },
            {
              headers: {
                clientToken: `Bearer ${accessToken}`,
                Accept: "application/json",
              },
              signal: controller.signal,
            }
          );
          
          console.log("API Response:", response);
          
          if (response.status === 200) {
            const botResponse = {
              type: "bot",
              content: response.data.content,
              timestamp: new Date(),
            };
            
            if (isAiChat) {
              updateAiChatMessages(botResponse);
            }
    
            setMessages((prevMessages) => [...prevMessages, botResponse]);
          } else {
            console.error("Error from API:", response.status);
          }
          */
    
          // ENHANCED BOLOFY Q&A LOGIC
          const getBolofyResponse = (userInput) => {
            const input = userInput.toLowerCase().trim();
            
            // Greeting patterns
            if (input.match(/^(hello|hi|hey|good morning|good afternoon|good evening|greetings)/i)) {
              return "Hello there how can i help you today?";
            }
            
            // Company information patterns
            if (input.match(/(what does bolofy do|tell me about bolofy|what is bolofy|bolofy business|what services does bolofy offer|what kind of company is bolofy|explain bolofy|bolofy services|what solutions does bolofy provide)/i)) {
              return "Bolofy is a voice solutions provider which provides AI receptionists and chatbots to SMEs";
            }
            
            // Location patterns
            if (input.match(/(where is the office of bolofy|where is bolofy located|office address|where can i find bolofy|where are you based|what's your location|bolofy office|office location|where is your office|headquarters|address)/i)) {
              return "Its located in Indiranagar, Bangalore";
            }
            
            // Additional services patterns
            if (input.match(/(what other services does bolofy provide|do you have any other offerings|what else can bolofy do|are there additional services|what about enterprise solutions|do you provide custom solutions|other services|additional services|custom ai solutions|enterprise services|more services)/i)) {
              return "Bolofy also provides custom AI solutions to enterprises";
            }
            
            // Contact/Support patterns
            if (input.match(/(how can i contact|contact number|phone number|call|reach out|get in touch|support)/i)) {
              return "You can reach us at +91 9035163809 for any assistance!";
            }
            
            // Pricing/Cost patterns
            if (input.match(/(pricing|cost|how much|price|rates|charges|fees)/i)) {
              return "For pricing information, please contact us at +91 9035163809 and our team will provide you with detailed pricing based on your requirements.";
            }
            
            // AI Receptionist specific questions
            if (input.match(/(ai receptionist|virtual receptionist|automated receptionist|how does ai receptionist work)/i)) {
              return "Our AI receptionists can handle calls, schedule appointments, answer common questions, and provide 24/7 customer support for your business.";
            }
            
            // Chatbot specific questions
            if (input.match(/(chatbot|chat bot|how does chatbot work|chatbot features)/i)) {
              return "Our chatbots can be integrated into your website to handle customer inquiries, provide information, and guide visitors through your services automatically.";
            }
            
            // SME/Business related questions
            if (input.match(/(sme|small medium enterprise|small business|medium business|business solutions)/i)) {
              return "We specialize in providing affordable AI solutions specifically designed for Small and Medium Enterprises to help them automate customer interactions.";
            }
            
            // Fallback to contextual responses based on keywords
            if (input.match(/(help|assist|support|need)/i)) {
              return "I'm here to help you! You can ask me about Bolofy's services, our location, pricing, or how our AI solutions can benefit your business.";
            }
            
            if (input.match(/(thank you|thanks|appreciate)/i)) {
              return "You're welcome! Is there anything else you'd like to know about Bolofy's services?";
            }
            
            // Generic fallback responses for unmatched queries
            const genericResponses = [
              "That's an interesting question! Let me think about that for a moment.",
              "I understand what you're asking. Here's what I can tell you about that topic.",
              "Thank you for your message! I'm processing your request and will get back to you shortly.",
              "Great question! Based on what you've shared, here are some suggestions that might help.",
              "I appreciate you reaching out. Let me provide you with the information you need.",
              "That's a valid concern. Here's how I can assist you with that.",
              "I see what you're asking about. Let me break this down for you step by step."
            ];
            
            return genericResponses[Math.floor(Math.random() * genericResponses.length)];
          };
    
          // Simulate API delay (1-3 seconds)
          await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
          // Get contextual response based on user input
          const selectedResponse = getBolofyResponse(truncatedInput);
    
          console.log("Bot Response Selected:", selectedResponse);
          console.log("User Input:", truncatedInput);
    
          // Create bot response
          const botResponse = {
            type: "bot",
            content: selectedResponse,
            timestamp: new Date(),
          };
    
          if (isAiChat) {
            updateAiChatMessages(botResponse);
          }
    
          setMessages((prevMessages) => [...prevMessages, botResponse]);
    
        } catch (error) {
          console.error("Error details:", {
            message: error.message,
            response: error.response?.data?.content,
            status: error.response?.status,
            token: accessToken ? "Present" : "Missing",
          });
    
          const errorMsg = "Sorry, I encountered an error. Please try again or call us @ +91 9035163809";
    
          if (isAiChat) {
            updateAiChatMessages({
              type: "bot",
              content: errorMsg,
              timestamp: new Date(),
              isError: true,
            });
          }
    
          if (axios.isCancel(error)) {
            console.log("Request canceled:", error.message);
          } else {
            toast.error(errorMsg);
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                type: "bot",
                content: errorMsg,
                timestamp: new Date(),
                isError: true,
              },
            ]);
          }
        } finally {
          setIsTyping(false);
          setIsWaitingForResponse(false);
          setAbortController(null);
        }
      },
      [
        input,
        isWaitingForResponse,
        selectedCommandIndex,
        currentChatId,
        config.accessToken,
        filteredCommands,
        setMessages,
        setIsTyping,
        parentUrl,
        setIsWaitingForResponse,
        setAbortController,
        handleCommandClick,
      ]
    );

    const remainingChars = maxMessageLength - input.length;

    return (
      <>
        <div
          className="p-2 pb-0 rounded-b-lg"
          style={{
            backgroundColor: customBranding.actionBgColor,
            color: customBranding.actionTextColor,
          }}
        >
          <React.Suspense fallback={""}>
            {showContactForm && (
              <ContactForm
                showForm={showContactForm}
                setShowForm={setShowContactForm}
                setMessages={setMessages}
              />
            )}
            {showStatusForm && (
              <StatusForm
                showForm={showStatusForm}
                setShowForm={setShowStatusForm}
                setMessages={setMessages}
              />
            )}
            {showLoginForm && (
              <Login
                showForm={showLoginForm}
                setShowForm={setShowLoginForm}
                setMessages={setMessages}
                onLoginSuccess={handleLoginSuccess}
              />
            )}
            {showFeedbackForm && (
              <FeedbackForm
                showForm={showFeedbackForm}
                setShowForm={setShowFeedbackForm}
                setMessages={setMessages}
              />
            )}
            {showAppointmentForm && (
              <Appointment
                showForm={showAppointmentForm}
                setShowForm={setShowAppointmentForm}
                setMessages={setMessages}
              />
            )}
            {showTasksForm && (
              <Tasks
                showForm={showTasksForm}
                setShowForm={setShowTasksForm}
                setMessages={setMessages}
              />
            )}
            {showReminderForm && (
              <ReminderForm
                showForm={showReminderForm}
                setShowForm={setShowReminderForm}
                setMessages={setMessages}
              />
            )}
          </React.Suspense>

          <AnimatePresence>
  {!showContactForm &&
    !showFeedbackForm &&
    !showAppointmentForm &&
    !showTasksForm &&
    !showStatusForm &&
    !showReminderForm && (  // Add this line
      <motion.div
      className={`flex justify-center gap-2 md:text-sm text-xs font-medium items-center w-full mb-2 px-2`}
      >
        {!isLoggedIn && (
          <>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={toggleContactForm}
              className={`w-24 p-2 bg-gray-200 text-gray-800 hover:bg-[#2888b542] rounded-3xl flex justify-center items-center`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{buttonLabel('contact')}</span>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={toggleFeedbackForm}
              className={`w-24 p-2 bg-gray-200 text-gray-800 hover:bg-[#2888b542] rounded-3xl flex justify-center items-center`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{buttonLabel('feedback')}</span>
            </motion.button>

            {/* Add reminder button for non-logged in users */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={() => setShowReminderForm(true)}
              className={`w-24 p-2 bg-gray-200 text-gray-800 hover:bg-[#2888b542] rounded-3xl flex justify-center items-center`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{buttonLabel('reminder')}</span>
            </motion.button>

            {eventDetails && (
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                onClick={toggleAppointmentForm}
                className={`w-40 p-2 bg-gray-200 text-gray-800 hover:bg-[#2888b542] rounded-3xl flex justify-center items-center`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>{buttonLabel('appointment')}</span>
              </motion.button>
            )}
          </>
        )}

        {isLoggedIn && (
          <>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={toggleStatusForm}
              className={`w-24 p-2 bg-gray-200 text-gray-800 hover:bg-[#2888b542] rounded-3xl flex justify-center items-center`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{buttonLabel('status')}</span>
            </motion.button>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={toggleTasksForm}
              className={`w-24 p-2 bg-gray-200 text-gray-800 hover:bg-[#2888b542] rounded-3xl flex justify-center items-center`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{buttonLabel('tasks')}</span>
            </motion.button>

            {/* Add reminder button for logged in users too */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={() => setShowReminderForm(true)}
              className={`w-24 p-2 bg-gray-200 text-gray-800 hover:bg-[#2888b542] rounded-3xl flex justify-center items-center`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{buttonLabel('reminder')}</span>
            </motion.button>
          </>
        )}
      </motion.div>
    )}
</AnimatePresence>

          <form onSubmit={handleSubmit} className="p-2 pb-0 rounded-b-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative mb-2"
            >
              <motion.div
                whileHover={{ boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.5)" }}
                className={`flex items-center bg-white shadow-md rounded-full border-slate-200 border-2 overflow-hidden  ${
                  isExpandedWindow ? "w-[48rem] mx-auto justify-center" : ""
                }`}
              >
                <motion.input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="flex-grow p-3 pl-5 pr-8 text-gray-700 bg-transparent  focus:outline-none text-sm "
                  placeholder={commonMessage('howCanIHelp')}
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                  disabled={isWaitingForResponse}
                  autoFocus
                />
                <div
                  className={`absolute  transform -translate-y-1/2 text-xs text-gray-400 ${
                    isExpandedWindow ? " left-[59rem] top-8" : "right-12 top-8"
                  } `}
                >
                  {remainingChars}
                </div>
                <motion.button
                  type="submit"
                  disabled={input.trim() === "" || isWaitingForResponse}
                  className={`${
                    input.trim() === "" || isWaitingForResponse
                      ? "bg-gray-300 cursor-not-allowed"
                      : ""
                  } text-white p-3 rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all duration-300`}
                  style={{
                    backgroundColor:
                      input.trim() === "" || isWaitingForResponse
                        ? ""
                        : config.color,
                  }}
                >
                  <IoArrowUp size={20} />
                </motion.button>
              </motion.div>
              <AnimatePresence>
                {showSuggestions && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="relative z-10 w-full bg-white shadow-lg rounded-md mt-1 overflow-hidden"
                  >
                    {filteredCommands.map((command, index) => (
                      <div
                        key={command.name}
                        className={`px-4 py-2 cursor-pointer ${
                          index === selectedCommandIndex
                            ? "bg-gray-200"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => handleCommandClick(command.name)}
                      >
                        /{command.name} - {command.description}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </form>
        </div>
      </>
    );
  }
);


export default Chat;
